import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { API_URL, generateSessionId } from '../lib/utils';
import { Send, Loader2, MessageCircle, User } from 'lucide-react';

const ChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setMessages([{
      id: 'welcome',
      message: 'Hello! I\'m your Archon Insurance assistant. How can I help you today?',
      is_from_user: false,
      created_at: new Date().toISOString()
    }]);
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { id: `user-${Date.now()}`, message: input, is_from_user: true, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/chat/message?message=${encodeURIComponent(input)}&session_id=${sessionId}`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setMessages((prev) => [...prev, { id: `ai-${Date.now()}`, message: data.ai_response, is_from_user: false, created_at: new Date().toISOString() }]);
    } catch (error) {
      setMessages((prev) => [...prev, { id: `error-${Date.now()}`, message: 'Sorry, an error occurred. Please try again or email monte@archoninsurance.com', is_from_user: false, created_at: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = ['What types of insurance do you offer?', 'How do I file a claim?', 'What affects my premium?'];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <div className="page-banner">
        <div className="container-wide">
          <h1>CHAT SUPPORT</h1>
          <div className="breadcrumb"><Link to="/">Home</Link> &gt; Chat</div>
        </div>
      </div>

      <section className="content-section flex-1" data-testid="chat-content">
        <div className="container-narrow">
          
          {/* Chat Box */}
          <div className="border border-gray-200">
            {/* Header */}
            <div className="bg-[#1a365d] text-white px-4 py-3 flex items-center gap-3">
              <MessageCircle className="h-5 w-5" />
              <div>
                <p className="font-semibold text-sm">Archon Insurance Assistant</p>
                <p className="text-xs text-white/70">Powered by AI • Available 24/7</p>
              </div>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 bg-gray-50" data-testid="chat-messages">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex mb-4 ${msg.is_from_user ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex items-start gap-2 max-w-[80%] ${msg.is_from_user ? 'flex-row-reverse' : ''}`}>
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.is_from_user ? 'bg-[var(--gold)]' : 'bg-[#1a365d]'}`}>
                      {msg.is_from_user ? <User className="h-4 w-4 text-white" /> : <MessageCircle className="h-4 w-4 text-white" />}
                    </div>
                    <div className={`rounded px-4 py-2 ${msg.is_from_user ? 'bg-[var(--gold)] text-white' : 'bg-white border border-gray-200'}`}>
                      <p className="text-sm">{msg.message}</p>
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-full bg-[#1a365d] flex items-center justify-center">
                    <MessageCircle className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded px-4 py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-[#1a365d]" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {messages.length <= 1 && (
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Suggested:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((q) => (
                    <button key={q} onClick={() => setInput(q)} className="text-xs bg-white border border-gray-200 px-3 py-1 rounded hover:border-[#1a365d]">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-gray-200 p-4 bg-white">
              <form onSubmit={sendMessage} className="flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message..." className="form-input flex-1" disabled={loading} data-testid="chat-input" />
                <button type="submit" disabled={!input.trim() || loading} className="btn-navy px-4" data-testid="chat-send-btn">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            AI cannot provide binding quotes or legal advice. Email: monte@archoninsurance.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChatPage;
